import { isFolderUpdatableByUser } from '@mimir/common/util/itemAccessControl';
import type { Platform } from '@mimir/common/context/applicationContext';
import type { FolderDto } from '@mimir/common/dto/folderModels';
import type UserDto from '@mimir/common/dto/userDto';

type SourceFolderAccessInfo = Pick<FolderDto, 'name' | 'updatableBy' | 'owner' | 'kind'>;

export type SourceFolderMoveCheck =
  | { canMoveOut: true }
  | { canMoveOut: false; folderName: string };

/**
 * Moving an item requires update permission on the source folder because it
 * removes the item from it. Copying doesn't, so it's allowed from protected
 * folders.
 *
 * If the folder can't be fetched, allow the drag and let the server handle
 * the permission check.
 */
export const checkSourceFolderMoveOut = async (
  sourceFolderId: string,
  user: UserDto & { tenantId: string },
  fetchFolder: (folderId: string) => Promise<SourceFolderAccessInfo>,
): Promise<SourceFolderMoveCheck> => {
  try {
    const sourceFolder = await fetchFolder(sourceFolderId);
    if (isFolderUpdatableByUser(sourceFolder.updatableBy, sourceFolder.owner, sourceFolder.kind, user)) {
      return { canMoveOut: true };
    }
    return { canMoveOut: false, folderName: sourceFolder.name };
  } catch {
    return { canMoveOut: true };
  }
};

/**
 * Explains why a move operation via drag from an update-protected folder was refused
 * and how to retry it as a copy by toggling the modifier key.
 */
export const getBlockedMoveOutOfFolderMessage = (platform: Platform, folderName: string, isHoldingCtrlOrAlt: boolean): string => {
  const modifier = isHoldingCtrlOrAlt ? 'without holding' : 'holding';
  // On a Mac, Ctrl-drag is a basically a right-click, so we never
  // get the drop. Only Option works there, so that's the only key worth naming.
  const modifierKeys = platform.isMac ? 'Option' : 'Ctrl or Alt';
  return `Moving items out of '${folderName}' requires update permission on that folder. Please drag again ${modifier} the '${modifierKeys}' key to copy instead.`;
};
